import { useMemo, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import {
  EMPTY_FILTER_QUERY,
  PAGE_SIZE,
} from "byzantine/src/dbbl/businessLogic/entities/paymentActivity";
import { FILTER_CONFIG } from "./Filters/filterConfig";
import useSearchAndFilter from "./useSearchAndFilter";

const useSearchAndFiltersFromQueryParams = () => {
  const [searchParams] = useSearchParams();
  const queryString = searchParams.toString();

  return useMemo(() => {
    const search = searchParams.get("search") || "";
    const filters: Record<string, any> = {};

    Object.values(FILTER_CONFIG).forEach((config) => {
      if (config.type === "multi-select" || config.type === "static") {
        const { queryKey } = config;
        // template_version is not a URL param — only ?template= is supported
        if (queryKey === "template_version") return;
        const queryParams = searchParams.get(queryKey);
        if (queryParams) {
          filters[queryKey] = queryParams.split(",");
        }
      } else if (config.type === "range") {
        const { min: minQueryKey, max: maxQueryKey } = config.queryKeys;
        const minVal = searchParams.get(minQueryKey);
        const maxVal = searchParams.get(maxQueryKey);
        if (minVal) {
          filters[minQueryKey] = minVal;
        }
        if (maxVal) {
          filters[maxQueryKey] = maxVal;
        }
      }
    });

    // parse ?template=<group_uuid> for linking from the template drawer
    const templateParam = searchParams.get("template");
    if (templateParam) {
      filters.template = templateParam;
    }

    const normalizedFilters = {
      ...EMPTY_FILTER_QUERY,
      ...filters,
    };

    return { search, filters: normalizedFilters };
  }, [queryString]);
};

type PaymentActivityConfigType = {
  useFetchPaginated: () => {
    send: (
      page: number,
      offset: number,
      pageSize: number,
      queryString: string,
      // ordering is reserved for future dynamic ordering support and is not yet passed by this hook
      ordering?: string,
      disableCaching?: boolean
    ) => unknown;
    loading: boolean;
  };
  useFormattedPaginated: (page: number) => unknown[];
  useTotal: () => number;
  useBootstrap: () => { loading: boolean; load: () => void };
  /** Optional query string prepended to every fetch (e.g. "has_organization_file=true") */
  baseQueryString?: string;
};

const usePaymentActivityContainer = (config: PaymentActivityConfigType) => {
  const { search: queryParamSearch, filters: queryParamFilters } =
    useSearchAndFiltersFromQueryParams();

  const {
    state,
    pageNumber,
    offsetIndex,
    queryString: filterQueryString,
    handleSearchChange,
    handleSetFilters,
    handleReset,
    resetFromParams,
    createPagination,
  } = useSearchAndFilter({
    initialFilters: queryParamFilters,
    initialSearch: queryParamSearch,
    pageSize: PAGE_SIZE,
  });

  const combinedQueryString = useMemo(() => {
    if (!config.baseQueryString) return filterQueryString;
    return filterQueryString
      ? `${config.baseQueryString}&${filterQueryString}`
      : config.baseQueryString;
  }, [config.baseQueryString, filterQueryString]);

  const { send: fetchPage, loading: paymentsLoading } =
    config.useFetchPaginated();
  const formattedPaginatedPayments = config.useFormattedPaginated(pageNumber);
  const total = config.useTotal();
  const { loading: bootstrapLoading, load } = config.useBootstrap();

  useEffect(() => {
    load();
  }, [load]);

  // fetch data when page number or combinedQueryString changes
  useEffect(() => {
    fetchPage(pageNumber, offsetIndex, PAGE_SIZE, combinedQueryString);
  }, [pageNumber, offsetIndex, combinedQueryString, fetchPage]);

  // scroll to top on navigation
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pageNumber, combinedQueryString]);

  // update filters and search when URL query params changes
  useEffect(() => {
    resetFromParams(queryParamFilters, queryParamSearch);
  }, [queryParamFilters, queryParamSearch, resetFromParams]);

  const refetchCurrentPage = useCallback(
    (options?: { disableCaching?: boolean }) =>
      fetchPage(
        pageNumber,
        offsetIndex,
        PAGE_SIZE,
        combinedQueryString,
        undefined,
        options?.disableCaching
      ),
    [fetchPage, pageNumber, offsetIndex, combinedQueryString]
  );

  const pagination = createPagination(total);

  return {
    state,
    formattedPaginatedPayments,
    paymentsLoading,
    bootstrapLoading,
    pagination,
    handleSearchChange,
    handleSetFilters,
    handleReset,
    refetchCurrentPage,
  };
};

export default usePaymentActivityContainer;
